import React, { useEffect, useState } from "react";
import { doc, updateDoc, collection, onSnapshot, orderBy,query,Timestamp } from "firebase/firestore";
import { db } from "../../firebase";
import "./css/Contador.css";
import { useDispatch } from "react-redux";
import { hideLoading, showLoading } from "../../../redux/actions/loadingActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { faStar as faStarSolid } from "@fortawesome/free-solid-svg-icons";
import { faStar as faStarRegular } from "@fortawesome/free-regular-svg-icons";

interface Log {
    id: string;
    totalAfter: number;
    createdAt: Timestamp;
    favorite?: boolean;
}

const ContadorLogs: React.FC = () => {
    const dispatch = useDispatch();
    const [logs, setLogs] = useState<Log[]>([]);
    const [openMonths, setOpenMonths] = useState<Record<string, boolean>>({});
    const [favoriteMode, setFavoriteMode] = useState(false);
    const [animatingStars, setAnimatingStars] = useState<Record<string, boolean>>({});

    useEffect(() => {
        dispatch(showLoading());

        const q = query(
            collection(db, "counter_logs"),
            orderBy("createdAt", "desc")
        );

        const unsubscribe = onSnapshot(q, (snap) => {
            const data = snap.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            })) as Log[];

            setLogs(data);
            dispatch(hideLoading());
        });

        return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const unsubCounter = onSnapshot(doc(db, "counter", "main"), (snap) => {
            const data = snap.data();
            if (data?.favoriteMode !== undefined) {
                setFavoriteMode(data.favoriteMode);
            }
        });

        return unsubCounter;
    }, []);

    const toggleFavorite = async (log: Log) => {
        if (!favoriteMode) return;

        // 🔥 activar animación
        setAnimatingStars((prev) => ({
            ...prev,
            [log.id]: true,
        }));

        setTimeout(() => {
            setAnimatingStars((prev) => ({
                ...prev,
                [log.id]: false,
            }));
        }, 350);

        // 🎉 (opcional) confetti SOLO al marcar como favorito
        if (!log.favorite) {
            import("canvas-confetti").then((confetti) => {
                confetti.default({
                    particleCount: 20,
                    spread: 50,
                    origin: { y: 0.6 },
                });
            });
        }

        await updateDoc(doc(db, "counter_logs", log.id), {
            favorite: !log.favorite,
        });
    };

    const toggleFavoriteMode = async () => {
        await updateDoc(doc(db, "counter", "main"), {
            favoriteMode: !favoriteMode,
        });
    };

    const isCurrentMonth = (date: Date) => {
        const now = new Date();

        return (
            date.getMonth() === now.getMonth() &&
            date.getFullYear() === now.getFullYear()
        );
    };

    const groupByMonth = (logs: Log[]) => {
        return logs.reduce<Record<string, Log[]>>((acc, log) => {
            const d = log.createdAt.toDate();
            const key = `${d.getFullYear()}-${d.getMonth()}`; // ej: 2026-0

            if (!acc[key]) acc[key] = [];
            acc[key].push(log);

            return acc;
        }, {});
    };

    const currentMonthLogs = logs.filter((log) =>
        isCurrentMonth(log.createdAt.toDate())
    );

    const oldLogs = logs.filter(
        (log) => !isCurrentMonth(log.createdAt.toDate())
    );

    const groupedOldLogs = groupByMonth(oldLogs);

    return (
        <div className="container-fluid">
            <div className="text-center">
                <h2 className="pb-3 page-heading">Registros del Amor</h2>
            </div>

            <div className="table-wrapper">
                <table className="simple-table">
                    <thead>
                        <tr>
                            <th>Número</th>
                            <th>Fecha</th>
                            <th>
                                <label className="custom-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={favoriteMode}
                                        onChange={toggleFavoriteMode}
                                    />
                                    <span className="checkmark"></span>
                                </label>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* MES ACTUAL */}
                        {currentMonthLogs.map((log) => (
                            <tr key={log.id}>
                                <td>{log.totalAfter}</td>
                                <td>
                                    {log.createdAt.toDate().toLocaleString("es-ES", {
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                    })}
                                </td>
                               <td>
                                    {favoriteMode && (
                                        <FontAwesomeIcon
                                            icon={log.favorite ? faStarSolid : faStarRegular}
                                            onClick={() => toggleFavorite(log)}
                                            className={`star-icon ${animatingStars[log.id] ? "animate" : ""}`}
                                            style={{
                                                cursor: "pointer",
                                                color: log.favorite ? "#f5c518" : "#ccc",
                                                transition: "0.2s",
                                            }}
                                        />
                                    )}
                                </td>
                            </tr>
                        ))}

                        {/* MESES ANTERIORES */}
                        {Object.entries(groupedOldLogs).map(([monthKey, monthLogs]) => {
                            const [year, month] = monthKey.split("-");
                            const monthName = new Date(
                                Number(year),
                                Number(month)
                            ).toLocaleString("es-ES", { month: "long", year: "numeric" }).toUpperCase();

                            const isOpen = openMonths[monthKey];

                            return (
                                <React.Fragment key={monthKey}>
                                    <tr
                                        className="month-group"
                                        onClick={() =>
                                            setOpenMonths((prev) => ({
                                                ...prev,
                                                [monthKey]: !prev[monthKey],
                                            }))
                                        }
                                    >
                                        <td colSpan={3}>
                                            <div className="month-row">
                                                <span className="month-title">{monthName}</span>
                                               <span className="month-icon">
                                                    <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
                                                </span>
                                            </div>
                                        </td>
                                    </tr>

                                    {isOpen &&
                                        monthLogs.map((log) => (
                                            <tr key={log.id} className="old-log">
                                                <td>{log.totalAfter}</td>
                                                <td>
                                                    {log.createdAt.toDate().toLocaleString("es-ES", {
                                                        day: "2-digit",
                                                        month: "2-digit",
                                                        year: "numeric",
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    })}
                                                </td>
                                                <td>
                                                    {favoriteMode && (
                                                        <FontAwesomeIcon
                                                            icon={log.favorite ? faStarSolid : faStarRegular}
                                                            onClick={() => toggleFavorite(log)}
                                                            className={`star-icon ${animatingStars[log.id] ? "animate" : ""}`}
                                                            style={{
                                                                cursor: "pointer",
                                                                color: log.favorite ? "#f5c518" : "#ccc",
                                                                transition: "0.2s",
                                                            }}
                                                        />
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                </React.Fragment>
                            );
                        })}

                        {logs.length === 0 && (
                            <tr>
                                <td colSpan={3} className="empty">
                                    No hay registros
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ContadorLogs;
